<template>
  <div class="price-box">
    <div class="price-box-header">
      <h3 class="price-box-title">{{ $t('experiences.price') }}</h3>
      <p v-if="priceType === 'PerPerson'" class="price-box-subtitle">{{ $t('experiences.perPerson') }}</p>
    </div>
    <div class="price-box-content">
      <!-- Prices differ across open dates (price periods) and a slot is chosen: that date's prices -->
      <template v-if="hasVariablePrices && selectedSlot">
        <p class="price-box-date">{{ $t('experiences.pricesForDate', { date: formatDate(selectedSlot.date) }) }}</p>
        <div v-if="priceType === 'PerPerson' && selectedBracketPrices.length" class="age-bracket-prices">
          <div v-for="bracket in selectedBracketPrices" :key="bracket.key" class="age-bracket-price-item">
            <span class="bracket-label">{{ getAgeBracketLabel(bracket.key) }}:</span>
            <span class="bracket-price">{{ bracket.price > 0 ? formatPrice(bracket.price) : $t('experiences.free') }}</span>
          </div>
        </div>
        <div v-else class="price-single-value">
          <span class="price-value-large">{{ formatPrice(selectedSlot.price) }}</span>
        </div>
      </template>

      <!-- PerPerson: always each age bracket's price (never "a partir de", which would hide what a child pays) -->
      <template v-else-if="priceType === 'PerPerson' && tableAgeBracketPrices">
        <div class="age-bracket-prices">
          <div
            v-for="(price, bracket) in tableAgeBracketPrices"
            :key="bracket"
            class="age-bracket-price-item"
            v-show="price > 0"
          >
            <span class="bracket-label">{{ getAgeBracketLabel(bracket) }}:</span>
            <span class="bracket-price">{{ formatPrice(price) }}</span>
          </div>
        </div>
        <p v-if="hasVariablePrices" class="price-box-note">{{ $t('experiences.priceVaries') }}</p>
      </template>

      <!-- PerExperience: prices differ across open dates (price periods) -->
      <template v-else-if="hasVariablePrices">
        <div class="price-single-value">
          <span class="price-value-large">{{ $t('experiences.fromPrice', { price: formatPrice(fromPrice) }) }}</span>
        </div>
        <p class="price-box-note">{{ $t('experiences.priceVariesByDate') }}</p>
      </template>

      <!-- PerExperience single price -->
      <div v-else-if="price > 0" class="price-single-value">
        <span class="price-value-large">{{ formatPrice(price) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { pricedAgeBrackets } from '@/helpers/agePrices.js';
import { parseDateString } from '@/services/slotAvailability.service.js';

// Price box order: youngest to oldest
const TABLE_ORDER = ['ZeroToFive', 'SixToEleven', 'TwelveToSeventeen', 'Adult', 'Senior'];

export default {
  name: 'PriceBox',
  props: {
    priceType: {
      type: String,
      default: 'PerExperience'
    },
    price: {
      type: Number,
      default: 0
    },
    ageBracketPrices: {
      type: Object,
      default: null
    },
    hasVariablePrices: {
      type: Boolean,
      default: false
    },
    fromPrice: {
      type: Number,
      default: 0
    },
    currency: {
      type: String,
      default: 'EUR'
    },
    // The slot the customer picked; its prices are those of its date
    selectedSlot: {
      type: Object,
      default: null
    },
    // Age-bracket prices shared by every bookable date (null when they differ or are unknown)
    openDatesAgeBracketPrices: {
      type: Object,
      default: null
    }
  },
  computed: {
    // Prices the open dates actually use when they all match; otherwise the base prices
    tableAgeBracketPrices() {
      const prices = this.openDatesAgeBracketPrices || this.ageBracketPrices;
      if (!prices) return null;
      // Youngest to oldest, whichever source the prices came from
      const ordered = {};
      TABLE_ORDER
        .filter(key => prices[key] !== undefined && prices[key] !== null)
        .forEach(key => { ordered[key] = prices[key]; });
      return ordered;
    },
    selectedBracketPrices() {
      // Same youngest-to-oldest order as the table above
      return pricedAgeBrackets(this.selectedSlot?.ageBracketPrices)
        .sort((a, b) => TABLE_ORDER.indexOf(a.key) - TABLE_ORDER.indexOf(b.key));
    }
  },
  methods: {
    formatDate(date) {
      const dateObj = parseDateString(date);
      return dateObj && !isNaN(dateObj.getTime()) ? dateObj.toLocaleDateString(this.$i18n.locale) : date;
    },
    formatPrice(value) {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2
      }).format(value);
    },
    getAgeBracketLabel(bracket) {
      const labels = {
        'ZeroToFive': this.$t('reservation.ageBrackets.zerotofive') || '0-5 years',
        'SixToEleven': this.$t('reservation.ageBrackets.sixtoeleven') || '6-11 years',
        'TwelveToSeventeen': this.$t('reservation.ageBrackets.twelvetoseventeen') || '12-17 years',
        'Adult': this.$t('reservation.ageBrackets.adult') || 'Adult',
        'Senior': this.$t('reservation.ageBrackets.senior') || 'Senior'
      };
      return labels[bracket] || bracket;
    }
  }
};
</script>

<style scoped>
.price-box {
  background: #ffffff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: sticky;
  top: 2rem;
  width: 100%;
  max-width: 100%;
  height: fit-content;
}

.price-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
}

.price-box-header {
  text-align: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f3f4f6;
}

.price-box-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0;
  letter-spacing: -0.5px;
}

.price-box-subtitle {
  font-size: 0.75rem;
  font-weight: 400;
  color: #9ca3af;
  margin: 0.25rem 0 0 0;
  text-transform: lowercase;
}

.price-box-content {
  padding-top: 0.25rem;
}

.age-bracket-prices {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.age-bracket-price-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.8rem;
}

.age-bracket-price-item:last-child {
  border-bottom: none;
}

.bracket-label {
  color: #6b7280;
  font-weight: 500;
  font-size: 0.85rem;
}

@media (min-width: 1200px) {
  .bracket-label {
    margin-right: 2rem;
  }
}

.bracket-price {
  color: var(--secondary-color);
  font-weight: 700;
  font-size: 0.9rem;
}

.price-box-date {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-align: center;
  margin: 0 0 0.5rem 0;
}

.price-box-note {
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: center;
  margin: 0.25rem 0 0 0;
}

.price-single-value {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 0;
}

.price-value-large {
  color: var(--secondary-color);
  font-weight: 700;
  font-size: 1.5rem;
  text-align: center;
}

@media (max-width: 768px) {
  .price-box {
    max-width: 100%;
    width: 100%;
    position: relative;
    top: 0;
  }
}
</style>
